import { Button, ButtonColor, ButtonSize, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CallCenterInterbank } from 'src/front/api/apiInstance';
import { CallCenterInterbankCurrentAccountsResponseData } from 'src/front/api/generated';
import {
  ButtonListRow,
  FormElement,
  FormRow,
  Separator,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import { BREAK_POINTS, TABLE_WIDTHS } from 'src/front/constant';
import { useAccountDispatch, useAccountState } from 'src/front/contexts/TransferHistoryContext';
import styled from 'styled-components';

const tableHeaders = [
  'general_components_accountNumber_text',
  'general_currency_label',
  'general_components_departmentCode_text',
  'general_components_availableBalance _text',
  'general_status_label',
  'general_components_openingDate_text',
];
export const CallCenterTableWrapper = styled.div`
  @media screen and (min-width: ${BREAK_POINTS.MIN.LARGE_2X}) {
    width: 1210px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_2X}) and (min-width: ${BREAK_POINTS.MIN
      .LARGE_1X}) {
    width: 970px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) and (min-width: ${BREAK_POINTS.MIN
      .TABLET}) {
    width: 870px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    width: 600px;
    overflow-x: scroll;
  }
`;
interface Props {
  onContinue: () => void;
  logScreen: () => Promise<void>;
}

const AccountSelectionForm: FC<Props> = ({ onContinue, logScreen }) => {
  const { t } = useTranslation();
  const { accountValues, accountPageNumber } = useAccountState();
  const dispatch = useAccountDispatch();
  const [hasAccountNextPage, setHasAccountNextPage] = useState<boolean>(false);
  const [accountList, setAccountList] = useState<CallCenterInterbankCurrentAccountsResponseData[]>(
    []
  );
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [accountPage, setAccountPage] = useState<number>(accountPageNumber);
  const [selectedAccount, setSelectedAccount] = useState<TableRow | undefined>(
    accountValues?.selectedAccount || undefined
  );
  const [selectedAccountWarning, setSelectedAccountWarning] = useState<boolean>(false);

  const loanAccountList = useCallback(
    async (pageNumber = accountPage, forceRefresh = false) => {
      setAccountsLoading(true);
      const { data } = await CallCenterInterbank.getCurrentAccounts({
        pageNumber,
        refresh: !pageNumber || forceRefresh,
        rowCountPerPage: 20,
      });
      if (data.result === 'OK') {
        setAccountList(data.accountList);
        setHasAccountNextPage(data.hasNextPage);
        setAccountPage(pageNumber || 1);
      }
      setAccountsLoading(false);
    },
    [accountPage]
  );
  useEffect(() => {
    logScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    loanAccountList(accountPage, true);
  }, [accountPage, loanAccountList, logScreen]);

  const onClick = async (): Promise<void> => {
    if (!selectedAccount) {
      setSelectedAccountWarning(true);
    } else {
      onContinue();
    }
  };
  const onAccountSelect = (item?: TableRow): void => {
    setSelectedAccountWarning(false);
    const selected = accountList.find((account) => account.accountNumber === item?.id);
    dispatch({
      accountPageNumber: accountPage,
      accountValues: {
        selectedAccount: item,
        accountNumber: selected?.accountNumber || '',
        currency: selected?.currency || '',
      },
    });

    setSelectedAccount(item);
  };
  const tableAccountList = accountList.map((account) => ({
    id: account.accountNumber,
    columns: [
      account.accountNumber,
      account.currency,
      account.departmentCode,
      account.availableBalance,
      account.status,
      account.openingDate,
    ],
  }));
  return (
    <>
      <Title>
        <Trans i18nKey="general_components_selectAccount_text" />
      </Title>
      <Separator />
      <CallCenterTableWrapper>
        <Table
          emptyTableText={t('general_components_notFoundAccount_errorMessage')}
          hasNextPage={hasAccountNextPage}
          headers={tableHeaders.map((head) => t(head))}
          items={tableAccountList}
          loading={accountsLoading}
          loadPageItems={loanAccountList}
          onSelect={onAccountSelect}
          pageNumber={accountPage}
          selectedItem={selectedAccount}
          tableWidth={TABLE_WIDTHS.DESKTOP_2X}
          widths={['20', '10', '20', '20', '15', '15']}
          clearItemOnPageChange
          withRadio
        />
      </CallCenterTableWrapper>
      <Separator />

      <FormRow>
        {selectedAccountWarning ? (
          <WarningText>
            <Trans i18nKey="general_components_selectAccount_text" />
          </WarningText>
        ) : null}
      </FormRow>

      <ButtonListRow>
        <FormElement>
          <Button
            color={ButtonColor.Primary}
            onClick={onClick}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
          />
        </FormElement>
      </ButtonListRow>
    </>
  );
};
export default AccountSelectionForm;
